"use client";

import React from "react";
import { useFormatter, useTranslations } from "next-intl";
import { CreditCard, Clock3, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  UserPlanOption,
  UserPlanVariant,
  UserSubscriptionStatus,
} from "@/lib/data/accounts/plans";

type CurrentPlanSummaryProps = {
  currentPlan: UserPlanOption | null;
  currentVariant: UserPlanVariant | null;
  subscriptionStatus: UserSubscriptionStatus;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  onManageBilling: () => void;
  isLoadingPortal?: boolean;
};

export const CurrentPlanSummary: React.FC<CurrentPlanSummaryProps> = ({
  currentPlan,
  currentVariant,
  subscriptionStatus,
  currentPeriodEnd,
  cancelAtPeriodEnd,
  onManageBilling,
  isLoadingPortal,
}) => {
  const t = useTranslations("pro.pricing");
  const formatter = useFormatter();

  if (currentPlan !== "PRO") return null;

  let renewalLabel = t("billing.not-available");
  if (currentPeriodEnd) {
    const formattedDate = formatter.dateTime(currentPeriodEnd, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    if (cancelAtPeriodEnd) {
      renewalLabel = t("billing.access-ends-on", { date: formattedDate });
    } else {
      renewalLabel = t("billing.renews-on", { date: formattedDate });
    }
  }

  const statusLabel = (() => {
    switch (subscriptionStatus) {
      case "ACTIVE":
        return t("sub-status.ACTIVE");
      case "TRIALING":
        return t("sub-status.TRIALING");
      case "PAST_DUE":
        return t("sub-status.PAST_DUE");
      case "UNPAID":
        return t("sub-status.UNPAID");
      case "CANCELED":
        return t("sub-status.CANCELED");
      default:
        return t("sub-status.DEFAULT");
    }
  })();

  const showStatusWarning =
    subscriptionStatus === "PAST_DUE" || subscriptionStatus === "UNPAID";

  if (!currentVariant) return null;
  return (
    <div className="mt-6 rounded-xl border border-primary/20 bg-white px-4 py-3 md:px-5 md:py-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CreditCard className="h-5 w-5" />
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold md:text-base">
                {t(`plans.${currentVariant}.title`)}
              </p>

              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                {t(`plans.${currentVariant}.interval`)}
              </span>

              {cancelAtPeriodEnd && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                  {t("will-not-renew")}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground md:text-sm">
              <Clock3 className="h-3.5 w-3.5" />
              <span>{renewalLabel}</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground md:text-sm">
              {showStatusWarning && (
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
              )}
              <span>{statusLabel}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 md:pt-0">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-full md:w-auto"
            onClick={onManageBilling}
            disabled={isLoadingPortal}
          >
            {isLoadingPortal ? t("billing.opening") : t("billing.manage")}
          </Button>
        </div>
      </div>
    </div>
  );
};
